import React, { useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import styles from './results-section.module.scss'

const countGender = (students, gender) => students.filter(student => student.gender === gender).length

/**
 * One resulting course as a board column: a header with its totals (students, H/M split, % with a
 * chosen classmate) and one card per student (current course, chosen classmates placed with
 * them). Gender is only shown as course totals, never on a student's card - the school asked not to
 * single out each student's gender. The whole column is a native HTML5 drop zone and every card is draggable - dropping a
 * card fires `onDropStudent(resultStudentId, courseName)`; no library needed for a plain "move a
 * card between lists" interaction. Cards moved but not saved yet (`movedStudentIds`) are
 * highlighted so the counselor can see what "Guardar cambios" will apply.
 */
const CourseColumn = ({ course, movedStudentIds, onDropStudent, isEditable = true }) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = (event) => {
    if (!isEditable) return
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDrop = (event) => {
    if (!isEditable) return
    event.preventDefault()
    setIsDragOver(false)
    const resultStudentId = event.dataTransfer.getData('text/plain')
    if (resultStudentId) onDropStudent(resultStudentId, course.name)
  }

  return (
    <div
      data-testid={`course-column-${course.id}`}
      className={classNames(styles.course_column, { [styles.drag_over]: isDragOver })}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <div className={styles.course_column_header}>
        <p className={styles.course_column_title}>{course.name}</p>
        <p className={styles.course_column_stats}>
          {t('resultColumnStats', {
            count: course.studentCount,
            male: countGender(course.students, 'H'),
            female: countGender(course.students, 'M')
          })}
        </p>
        <p className={styles.course_column_stats}>
          {course.preferencesAchievedPercent === null
            ? t('resultNoPreferencesData')
            : t('resultPreferencesAchieved', { percent: course.preferencesAchievedPercent })}
        </p>
      </div>
      <ul className={styles.student_cards}>
        {course.students.map(student => (
          <li
            key={student.id}
            data-testid={`student-card-${student.id}`}
            draggable={isEditable}
            onDragStart={(event) => event.dataTransfer.setData('text/plain', String(student.id))}
            className={classNames(styles.student_card, {
              [styles.draggable_student]: isEditable,
              [styles.moved_student]: movedStudentIds?.has(String(student.id))
            })}
          >
            <p className={styles.student_name}>{student.name}</p>
            <p className={styles.student_detail}>
              {student.previousCourseName}
            </p>
            {student.preferencesSelectedCount !== null && student.preferencesSelectedCount !== undefined && (
              <p className={styles.student_detail}>
                {t('resultCardClassmatesAchieved', {
                  achieved: student.preferencesAchievedCount ?? 0,
                  selected: student.preferencesSelectedCount
                })}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default CourseColumn
